/**
 * CPD-662. What the Goals Report calls a member, and what it refuses to call
 * them.
 *
 * The identity band prints `rank.title` as the page's largest words. That is
 * fine until the value present is not a rank, and two of those reach it:
 *
 *  - The skills survey stores a PLACEHOLDER rank row when a member skips the
 *    optional rank question, because the server's completion check needs a
 *    row. Its title is the literal string below, so the report was headed
 *    "Not given". The band already drops parts that are absent, and a
 *    placeholder is not absent, so that guard could never help.
 *  - A thin or seeded record can carry single-character stand-ins.
 *
 * There is no member name to fall back to: CPD does not hold one reliably.
 * So the fallback is a plain noun for the person reading their own report.
 */

/**
 * The rank title the skills survey writes when a member gives none.
 *
 * Declared here rather than in the survey so the two places that care agree
 * on one spelling. The survey WRITES it; the report must not PRINT it.
 */
export const RANK_NOT_GIVEN_TITLE = 'Not given';

/** What the Goals Report calls a member when no rank is usable. */
export const MEMBER_FALLBACK_NAME = 'Learner';

/**
 * Real pay grades are a rank band and a number: E-8, O-3, W-2. A bare digit
 * is a stand-in, and bracketing it after the heading turns it into something
 * that looks like a grade and is not.
 */
const PAY_GRADE = /^[EOW]-?\d{1,2}$/i;

/** Whether a stored pay grade is shaped like one, and so worth printing. */
export function isPayGrade(value: string | null | undefined): boolean {
  return PAY_GRADE.test((value ?? '').trim());
}

/**
 * The rank title to show, or empty when what is stored is not one.
 *
 * Only the survey's own placeholder is rejected by name. Anything else a
 * member or an import put there is shown as given: we cannot tell a thin
 * record from an unusual one, and guessing would hide real ranks.
 */
export function usableRankTitle(title: string | null | undefined): string {
  const trimmed = (title ?? '').trim();
  return trimmed === RANK_NOT_GIVEN_TITLE ? '' : trimmed;
}

/**
 * CPD-662. The MOC row the survey writes for somebody who has NOT served.
 *
 * The server derives "skills survey complete" partly from a member having a
 * MOC row, so a civilian gets a marker one rather than none -- code "X",
 * title "Learner". Both the shipped survey and the old build-your-target step
 * write exactly these values; see CIVILIAN_SENTINEL in
 * skills-survey.component.ts and setProfileTypeCivilian in
 * military-skills-profile-type.component.ts.
 *
 * It then reached the Goals Report as a job code, so a civilian's own report
 * introduced them as "X Learner" under a heading of "Learner". Two renderings
 * of the same placeholder, one on top of the other, and neither of them
 * anything the member ever typed.
 *
 * The last round left this alone on the grounds that no rule could tell the
 * marker from a real code we do not recognise. There is one: the marker is a
 * constant this product writes, so it can be matched on BOTH fields at once.
 * A genuine MOC "X" with the genuine title "Learner" does not exist, and
 * requiring both means an unrecognised real code still prints.
 */
const CIVILIAN_MOC_CODE = 'X';
const CIVILIAN_MOC_TITLE = 'Learner';

export function isCivilianMocMarker(
  code: string | null | undefined,
  title: string | null | undefined
): boolean {
  return (
    (code ?? '').trim().toUpperCase() === CIVILIAN_MOC_CODE &&
    (title ?? '').trim() === CIVILIAN_MOC_TITLE
  );
}
